import React from 'react';
import { Play, Clock, Flame } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { resolveMediaUrl, formatDate, formatTime } from '@/utils/helpers';

const getHeatColor = (score) => {
  if (score >= 80) return 'text-red-400';
  if (score >= 60) return 'text-orange-400';
  if (score >= 40) return 'text-amber-400';
  if (score >= 20) return 'text-yellow-500';
  return 'text-gray-500';
};

const getHeatLabel = (score) => {
  if (score >= 80) return 'On Fire';
  if (score >= 60) return 'Hot';
  if (score >= 40) return 'Warm';
  if (score >= 20) return 'Mild';
  return '';
};

const NLS_PLUS_URL = 'https://plus.newlogicalsports.com';

export const GameCard = ({ game, onWatch, engagementScore }) => {
  const hasFreeSources = game.video_sources?.some(vs => vs.is_free);
  const hasPlusContent = game.video_sources?.some(vs => vs.type === 'uscreen');
  const primarySource = game.video_sources?.[0];
  const isSubscriptionGame = game.access_type === 'subscription' || game.access_type === 'ppv' || game.is_paywall;

  const handleClick = () => {
    // Temporarily redirect subscription/PPV games to NLS+ site
    if (isSubscriptionGame) {
      window.open(NLS_PLUS_URL, '_blank');
      return;
    }
    if (primarySource?.type === 'uscreen' && !primarySource?.is_free) {
      window.open(primarySource.url, '_blank');
    } else {
      onWatch(game);
    }
  };

  return (
    <div 
      className="game-card group cursor-pointer"
      onClick={handleClick}
      data-testid={`game-card-${game.id}`}
    >
      <div className="relative aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-t-xl overflow-hidden">
        {game.thumbnail ? (
          <img src={resolveMediaUrl(game.thumbnail)} alt={`${game.home_team_name} vs ${game.away_team_name}`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl mb-2">VS</div>
              <div className="text-sm text-gray-400">{game.league_name}</div>
            </div>
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="text-center">
            <Play className="w-12 h-12 text-white mx-auto mb-2" />
            <span className="text-white font-medium">
              {primarySource?.type === 'uscreen' && !primarySource?.is_free ? 'Watch on Plus' : 'Watch Now'}
            </span>
          </div>
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {game.status === 'live' && (
            <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>
          )}
          {hasFreeSources && (
            <Badge className="bg-green-500 text-white">FREE</Badge>
          )}
          {hasPlusContent && !hasFreeSources && (
            <Badge className="bg-sky-300 text-white">PLUS</Badge>
          )}
        </div>

        {/* Score (if live or finished) */}
        {(game.status === 'live' || game.status === 'finished') && game.home_score !== null && (
          <div className="absolute bottom-3 right-3 bg-black/80 px-3 py-1 rounded-lg">
            <span className="text-white font-bold">{game.home_score} - {game.away_score}</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-sky-200">{game.sport_name} / {game.league_name}</div>
          {engagementScore > 0 && (
            <div className={`flex items-center gap-1 ${getHeatColor(engagementScore)}`} title={`Engagement: ${engagementScore}/100`} data-testid={`engagement-score-${game.id}`}>
              <Flame className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">{getHeatLabel(engagementScore)}</span>
            </div>
          )}
        </div>
        <h3 className="text-white font-semibold mb-2">
          {game.home_team_name || 'TBD'} vs {game.away_team_name || 'TBD'}
        </h3>
        <div className="flex items-center text-gray-400 text-sm">
          <Clock className="w-4 h-4 mr-1" />
          {formatDate(game.scheduled_time)} at {formatTime(game.scheduled_time)}
        </div>
      </div>
    </div>
  );
};

export default GameCard;
